import React, { useState, useEffect } from 'react';
import SEOHead from '../components/SEOHead';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../utils/api';
import { 
    ChevronRightIcon, 
    HomeIcon,
    Squares2X2Icon,
    ListBulletIcon,
    MagnifyingGlassIcon,
    FunnelIcon
} from '@heroicons/react/24/outline';

// Kategori Kartı Bileşeni
const CategoryCard = ({ category }) => {
    return (
        <Link
            to={`/categories/${category.slug}`}
            className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden group"
        >
            <div className="p-6">
                {/* İkon veya Resim */}
                <div className="flex items-center justify-center h-32 mb-4 bg-gradient-to-br from-brand-green-light to-brand-green rounded-xl group-hover:scale-105 transition-transform">
                    {category.image_url ? (
                        <img 
                            src={category.image_url} 
                            alt={category.name}
                            className="w-full h-full object-cover rounded-xl"
                        />
                    ) : category.icon ? (
                        <i className={`${category.icon} text-4xl text-white`}></i>
                    ) : (
                        <Squares2X2Icon className="w-12 h-12 text-white" />
                    )}
                </div>

                {/* Kategori Bilgileri */}
                <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-brand-green transition-colors">
                    {category.name}
                </h3>
                
                {category.description && (
                    <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                        {category.description}
                    </p>
                )}

                <div className="flex items-center justify-between text-sm text-gray-500">
                    <span>{category.product_count || 0} ürün</span>
                    {category.children && category.children.length > 0 && (
                        <span>{category.children.length} alt kategori</span>
                    )}
                </div>

                {/* Alt Kategoriler Önizleme */}
                {category.children && category.children.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="flex flex-wrap gap-1">
                            {category.children.slice(0, 3).map((child) => (
                                <span 
                                    key={child.id}
                                    className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full"
                                >
                                    {child.name}
                                </span>
                            ))}
                            {category.children.length > 3 && (
                                <span className="px-2 py-1 bg-gray-100 text-gray-600 text-xs rounded-full">
                                    +{category.children.length - 3}
                                </span>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </Link>
    );
};

// Ürün Kartı Bileşeni
const ProductCard = ({ product, viewMode }) => {
    if (viewMode === 'list') {
        return (
            <Link
                to={`/products/${product.id}`}
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
            >
                <div className="w-20 h-20 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                    <img 
                        src={product.main_image_url || 'https://placehold.co/80x80'} 
                        alt={product.name}
                        className="w-full h-full object-cover"
                    />
                </div>
                <div className="flex-1">
                    <h3 className="font-medium text-gray-900 mb-1">{product.name}</h3>
                    <p className="text-gray-600 text-sm mb-2 line-clamp-2">{product.description}</p>
                    <div className="flex items-center justify-between">
                        <span className="text-lg font-bold text-brand-green">
                            ₺{parseFloat(product.price).toFixed(2)}
                        </span>
                        {product.category_names && (
                            <div className="flex gap-1">
                                {product.category_names.slice(0, 2).map((cat, index) => (
                                    <span 
                                        key={index}
                                        className="px-2 py-1 bg-white text-gray-600 text-xs rounded-full"
                                    >
                                        {cat}
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </Link>
        );
    }

    return (
        <Link
            to={`/products/${product.id}`}
            className="bg-gray-50 rounded-lg overflow-hidden hover:shadow-md transition-shadow group"
        >
            <div className="aspect-square bg-gray-200 overflow-hidden">
                <img 
                    src={product.main_image_url || 'https://placehold.co/300x300'} 
                    alt={product.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                />
            </div>
            <div className="p-4">
                <h3 className="font-medium text-gray-900 mb-2 line-clamp-2">{product.name}</h3>
                <div className="flex items-center justify-between">
                    <span className="text-lg font-bold text-brand-green">
                        ₺{parseFloat(product.price).toFixed(2)}
                    </span>
                    {product.category_names && product.category_names.length > 0 && (
                        <span className="px-2 py-1 bg-white text-gray-600 text-xs rounded-full">
                            {product.category_names[0]}
                        </span>
                    )}
                </div>
            </div>
        </Link>
    );
};

function CategoriesPage() {
    const { categorySlug } = useParams();
    const navigate = useNavigate();
    const [categories, setCategories] = useState([]);
    const [currentCategory, setCurrentCategory] = useState(null);
    const [products, setProducts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [productsLoading, setProductsLoading] = useState(false);
    const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOption, setSortOption] = useState('newest');

    // Ana kategorileri yükle
    useEffect(() => {
        const fetchCategories = async () => {
            try {
                const response = await api.get('/api/categories');
                setCategories(response.data || []);
            } catch (error) {
                console.error('Kategoriler yüklenirken hata:', error);
                toast.error('Kategoriler yüklenemedi');
            } finally {
                setLoading(false);
            }
        };

        fetchCategories();
    }, []);

    // Belirli kategori detayı ve ürünlerini yükle
    useEffect(() => {
        if (categorySlug) {
            fetchCategoryDetails();
            fetchCategoryProducts();
        } else {
            setCurrentCategory(null);
            setProducts([]);
        }
    }, [categorySlug, sortOption]);

    const fetchCategoryDetails = async () => {
        try {
            const response = await api.get(`/api/categories/${categorySlug}`);
            setCurrentCategory(response.data);
        } catch (error) {
            console.error('Kategori detayı yüklenirken hata:', error);
            toast.error('Kategori bulunamadı');
            navigate('/categories');
        }
    };

    const fetchCategoryProducts = async () => {
        setProductsLoading(true);
        try {
            const response = await api.get(`/api/categories/${categorySlug}/products`, {
                params: {
                    sort: sortOption,
                    limit: 50
                }
            });
            setProducts(response.data || []);
        } catch (error) {
            console.error('Kategori ürünleri yüklenirken hata:', error);
            toast.error('Ürünler yüklenemedi');
        } finally {
            setProductsLoading(false);
        }
    };

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchTerm.trim()) {
            navigate(`/products?search=${encodeURIComponent(searchTerm.trim())}`);
        }
    };

    const filteredCategories = categories.filter(category =>
        category.name.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="animate-pulse">
                        <div className="h-8 bg-gray-300 rounded w-1/4 mb-8"></div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {Array.from({ length: 8 }).map((_, i) => (
                                <div key={i} className="bg-white rounded-2xl shadow-lg p-6">
                                    <div className="h-32 bg-gray-300 rounded-xl mb-4"></div>
                                    <div className="h-6 bg-gray-300 rounded mb-2"></div>
                                    <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // Ana kategoriler görünümü
    if (!categorySlug) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Header */}
                    <div className="text-center mb-12">
                        <div className="inline-flex items-center justify-center w-16 h-16 bg-brand-green rounded-full mb-4 shadow-lg">
                            <Squares2X2Icon className="w-8 h-8 text-white" />
                        </div>
                        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
                            Kategoriler
                        </h1>
                        <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                            Bebeğiniz ve çocuğunuz için en kaliteli ürünleri kategorilere göre keşfedin
                        </p>
                    </div>

                    {/* Arama */}
                    <div className="max-w-md mx-auto mb-12">
                        <form onSubmit={handleSearch} className="relative">
                            <input
                                type="text"
                                placeholder="Kategori ara..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-brand-green focus:border-brand-green text-lg"
                            />
                            <MagnifyingGlassIcon className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                        </form>
                    </div>

                    {/* Kategoriler Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                        {filteredCategories.map((category) => (
                            <CategoryCard key={category.id} category={category} />
                        ))}
                    </div>

                    {filteredCategories.length === 0 && searchTerm && (
                        <div className="text-center py-12">
                            <div className="text-gray-500 text-lg">
                                `"{searchTerm}" için kategori bulunamadı`
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    }

    // SEO verilerini hazırla
    const generateSEOData = () => {
        if (currentCategory) {
            const categoryTitle = `${currentCategory.name} - Çocuk Giyim | Minela Kids`;
            const categoryDescription = currentCategory.description 
                ? `${currentCategory.description.substring(0, 150)}...` 
                : `${currentCategory.name} kategorisindeki en kaliteli çocuk giyim ürünlerini Minela Kids'te keşfedin. Ücretsiz kargo ve hızlı teslimat.`;
            
            const categorySchema = {
                "@context": "https://schema.org",
                "@type": "CollectionPage",
                "name": currentCategory.name,
                "description": categoryDescription,
                "url": `https://minelakids.com/categories/${currentCategory.slug}`,
                "mainEntity": {
                    "@type": "ItemList",
                    "numberOfItems": products.length,
                    "itemListElement": products.slice(0, 10).map((product, index) => ({
                        "@type": "Product",
                        "position": index + 1,
                        "name": product.name,
                        "url": `https://minelakids.com/products/${product.id}`,
                        "image": product.main_image_url,
                        "offers": {
                            "@type": "Offer",
                            "price": product.price,
                            "priceCurrency": "TRY"
                        }
                    }))
                }
            };

            return { categoryTitle, categoryDescription, categorySchema };
        } else {
            const allCategoriesTitle = "Tüm Kategoriler - Çocuk Giyim | Minela Kids";
            const allCategoriesDescription = "Minela Kids'te çocuk giyim kategorilerini keşfedin. Kız çocuk, erkek çocuk, bebek kıyafetleri ve daha fazlası için tüm kategorileri inceleyin.";
            
            const allCategoriesSchema = {
                "@context": "https://schema.org",
                "@type": "CollectionPage",
                "name": "Çocuk Giyim Kategorileri",
                "description": allCategoriesDescription,
                "url": "https://minelakids.com/categories"
            };

            return { 
                categoryTitle: allCategoriesTitle, 
                categoryDescription: allCategoriesDescription, 
                categorySchema: allCategoriesSchema 
            };
        }
    };

    const { categoryTitle, categoryDescription, categorySchema } = generateSEOData();

    // Kategori detay görünümü
    return (
        <>
            <SEOHead
                title={categoryTitle}
                description={categoryDescription}
                keywords={`${currentCategory?.name || 'çocuk giyim kategorileri'}, çocuk kıyafeti, minela kids, bebek giyim, çocuk moda, ${currentCategory?.gender || ''}`}
                ogType="website"
                structuredData={categorySchema}
                canonicalUrl={currentCategory ? `https://minelakids.com/categories/${currentCategory.slug}` : 'https://minelakids.com/categories'}
            />
            <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Breadcrumb */}
                    <nav className="flex items-center space-x-2 text-sm mb-8">
                        <Link to="/" className="text-gray-500 hover:text-brand-green">
                            <HomeIcon className="h-4 w-4" />
                        </Link>
                        <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                        <Link to="/categories" className="text-gray-500 hover:text-brand-green">
                            Kategoriler
                        </Link>
                        {currentCategory?.parent && (
                            <>
                                <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                                <Link 
                                    to={`/categories/${currentCategory.parent.slug}`}
                                    className="text-gray-500 hover:text-brand-green"
                                >
                                    {currentCategory.parent.name}
                                </Link>
                            </>
                        )}
                        <ChevronRightIcon className="h-4 w-4 text-gray-400" />
                        <span className="text-gray-900 font-medium">{currentCategory?.name}</span>
                    </nav>

                    {/* Kategori Header */}
                    {currentCategory && (
                        <div className="bg-white rounded-2xl shadow-xl p-6 sm:p-8 mb-8">
                            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
                                {currentCategory.image_url && (
                                    <div className="w-24 h-24 sm:w-32 sm:h-32 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0">
                                        <img 
                                            src={currentCategory.image_url} 
                                            alt={currentCategory.name}
                                            className="w-full h-full object-cover"
                                        />
                                    </div>
                                )}
                                <div className="flex-1">
                                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                                        {currentCategory.name}
                                    </h1>
                                    {currentCategory.description && (
                                        <p className="text-gray-600 text-lg mb-4">
                                            {currentCategory.description}
                                        </p>
                                    )}
                                    <div className="flex items-center gap-4 text-sm text-gray-500">
                                        <span>{currentCategory.product_count || 0} ürün</span>
                                        {currentCategory.children && currentCategory.children.length > 0 && (
                                            <span>{currentCategory.children.length} alt kategori</span>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Alt Kategoriler */}
                    {currentCategory?.children && currentCategory.children.length > 0 && (
                        <div className="mb-8">
                            <h2 className="text-xl font-bold text-gray-900 mb-4">Alt Kategoriler</h2>
                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-4">
                                {currentCategory.children.map((child) => (
                                    <Link
                                        key={child.id}
                                        to={`/categories/${child.slug}`}
                                        className="bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow p-4 text-center group"
                                    >
                                        {child.icon && (
                                            <div className="text-2xl mb-2 text-brand-green group-hover:scale-110 transition-transform">
                                                <i className={child.icon}></i>
                                            </div>
                                        )}
                                        <h3 className="font-medium text-gray-900 text-sm">{child.name}</h3>
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Ürünler Bölümü */}
                    <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
                        {/* Ürün Kontrolleri */}
                        <div className="border-b border-gray-200 p-4 sm:p-6">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <h2 className="text-xl font-bold text-gray-900">
                                    Ürünler ({products.length})
                                </h2>
                                
                                <div className="flex items-center gap-4">
                                    {/* Sıralama */}
                                    <select
                                        value={sortOption}
                                        onChange={(e) => setSortOption(e.target.value)}
                                        className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-brand-green focus:border-brand-green text-sm"
                                    >
                                        <option value="newest">En Yeniler</option>
                                        <option value="price_asc">Fiyat (Düşük → Yüksek)</option>
                                        <option value="price_desc">Fiyat (Yüksek → Düşük)</option>
                                        <option value="name_asc">İsim (A → Z)</option>
                                        <option value="name_desc">İsim (Z → A)</option>
                                    </select>

                                    {/* Görünüm Modu */}
                                    <div className="flex rounded-lg border border-gray-300 overflow-hidden">
                                        <button
                                            onClick={() => setViewMode('grid')}
                                            className={`p-2 ${viewMode === 'grid' ? 'bg-brand-green text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                        >
                                            <Squares2X2Icon className="h-4 w-4" />
                                        </button>
                                        <button
                                            onClick={() => setViewMode('list')}
                                            className={`p-2 ${viewMode === 'list' ? 'bg-brand-green text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                                        >
                                            <ListBulletIcon className="h-4 w-4" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Ürünler */}
                        <div className="p-4 sm:p-6">
                            {productsLoading ? (
                                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                    {Array.from({ length: 8 }).map((_, i) => (
                                        <div key={i} className="animate-pulse">
                                            <div className="h-48 bg-gray-300 rounded-lg mb-4"></div>
                                            <div className="h-4 bg-gray-300 rounded mb-2"></div>
                                            <div className="h-4 bg-gray-300 rounded w-3/4"></div>
                                        </div>
                                    ))}
                                </div>
                            ) : products.length === 0 ? (
                                <div className="text-center py-12">
                                    <div className="text-gray-500 text-lg">
                                        Bu kategoride henüz ürün bulunmuyor
                                    </div>
                                </div>
                            ) : (
                                <div className={viewMode === 'grid' 
                                    ? 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6'
                                    : 'space-y-4'
                                }>
                                    {products.map((product) => (
                                        <ProductCard 
                                            key={product.id} 
                                            product={product} 
                                            viewMode={viewMode}
                                        />
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}

export default CategoriesPage;
